import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const AdminNews = ({ onLogout }) => {
  const navigate = useNavigate();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newsForm, setNewsForm] = useState({
    title: '',
    content: '',
    type: 'news',
    image: null,
    imagePreview: null,
    start_date: '',
    end_date: '',
    location: ''
  });
  const [editingItem, setEditingItem] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [hasMore, setHasMore] = useState(false);
  const titleInputRef = useRef(null);

  // --- Notifications ---
  const showNotification = (msg, type = 'success') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 3000);
  };

  // --- Load news items ---
  const loadNewsItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('news_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) showNotification('Failed to load news items', 'error');
      else {
        setNewsItems(data || []);
        setHasMore((data || []).length >= 10);
      }
    } catch {
      showNotification('Error loading news items', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNewsItems();

    const subscription = supabase
      .channel('news-events')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'news_events' },
        payload => {
          setNewsItems(prev => {
            if (payload.eventType === 'INSERT') return [payload.new, ...prev];
            if (payload.eventType === 'UPDATE') return prev.map(item => item.id === payload.new.id ? payload.new : item);
            if (payload.eventType === 'DELETE') return prev.filter(item => item.id !== payload.old.id);
            return prev;
          });
        }
      )
      .subscribe();

    return () => subscription.unsubscribe();
  }, [loadNewsItems]);

  // --- Image selection & preview ---
  const handleImageChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      showNotification('File too large (max 4MB)', 'error');
      return;
    }
    if (newsForm.imagePreview) URL.revokeObjectURL(newsForm.imagePreview);
    setNewsForm({
      ...newsForm,
      image: file,
      imagePreview: URL.createObjectURL(file)
    });
  };

  // --- Ensure bucket exists ---
  const ensureBucketExists = async bucketName => {
    const { data: buckets } = await supabase.storage.listBuckets();
    if (!buckets.find(b => b.name === bucketName)) {
      await supabase.storage.createBucket(bucketName, { public: true });
    }
  };

  // --- Save or update news item ---
  const handleSaveNews = async () => {
    if (!newsForm.title.trim() || !newsForm.content.trim()) {
      showNotification('Please fill in all fields', 'error');
      return;
    }

    setUploadingImage(true);
    let imageUrl = newsForm.imagePreview;

    try {
      await ensureBucketExists('news');

      if (newsForm.image && newsForm.image instanceof File) {
        const rawName = newsForm.image.name || 'image';
        const safeFileName = rawName.replace(/\s+/g, '_').replace(/[^\w.-]/g, '');
        const filePath = `news_images/${Date.now()}_${safeFileName}`;
        const { error: uploadErr } = await supabase.storage
          .from('news')
          .upload(filePath, newsForm.image, { upsert: true, contentType: newsForm.image.type });
        if (uploadErr) {
          showNotification('Image upload failed: ' + uploadErr.message, 'error');
          setUploadingImage(false);
          return;
        }
        const { data: urlData } = supabase.storage.from('news').getPublicUrl(filePath);
        imageUrl = urlData.publicUrl;
      }

      if (editingItem) {
        const { error } = await supabase.from('news_events').update({
          ...newsForm,
          image_url: imageUrl
        }).eq('id', editingItem.id);
        if (error) showNotification('Failed to update: ' + error.message, 'error');
        else showNotification('News item updated!');
      } else {
        const { error } = await supabase.from('news_events').insert([{
          ...newsForm,
          image_url: imageUrl,
          is_active: true
        }]);
        if (error) showNotification('Failed to create news: ' + error.message, 'error');
        else showNotification('News item created!');
      }

      resetForm();
      loadNewsItems();
    } catch (err) {
      console.error(err);
      showNotification('Unexpected error: ' + err.message, 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const resetForm = () => {
    setNewsForm({
      title: '',
      content: '',
      type: 'news',
      image: null,
      imagePreview: null,
      start_date: '',
      end_date: '',
      location: ''
    });
    setEditingItem(null);
    setIsFormOpen(false);
  };

  const handleDeleteItem = id => {
    setConfirmMessage('Delete this news item?');
    setIsConfirmOpen(true);
    setConfirmAction(() => async () => {
      const { error } = await supabase.from('news_events').delete().eq('id', id);
      if (error) showNotification('Failed to delete: ' + error.message, 'error');
      else showNotification('News item deleted');
      loadNewsItems();
      setIsConfirmOpen(false);
    });
  };

  const handleSelectItem = id => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const handleSelectAll = () => {
    if (selectedItems.length === newsItems.length) setSelectedItems([]);
    else setSelectedItems(newsItems.map(i => i.id));
  };
  const handleBulkDelete = () => {
    if (!selectedItems.length) return;
    setConfirmMessage(`Delete ${selectedItems.length} selected items?`);
    setIsConfirmOpen(true);
    setConfirmAction(() => async () => {
      const { error } = await supabase.from('news_events').delete().in('id', selectedItems);
      if (error) showNotification('Bulk delete failed: ' + error.message, 'error');
      else showNotification(`${selectedItems.length} items deleted`);
      setSelectedItems([]);
      loadNewsItems();
      setIsConfirmOpen(false);
    });
  };

  const filteredNews = newsItems.filter(item => {
    return (
      (filterType === 'all' || item.type === filterType) &&
      item.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const formatEventDate = (start, end) => {
    if (!start) return '-';
    if (end && end !== start) return `${new Date(start).toLocaleDateString()} - ${new Date(end).toLocaleDateString()}`;
    return new Date(start).toLocaleDateString();
  };

  useEffect(() => {
    if (isFormOpen && titleInputRef.current) titleInputRef.current.focus();
  }, [isFormOpen]);

  return (
    <div className="min-h-screen bg-background-light p-4">
      {/* Notifications */}
      <div className="fixed top-4 right-4 space-y-2 z-50">
        {notifications.map(n => (
          <div key={n.id} className={`px-6 py-3 rounded-xl shadow-lg text-white ${n.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
            {n.msg}
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="bg-royal-blue text-white py-6 mb-8 rounded-xl shadow-lg flex justify-between items-center px-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="bg-gold text-white px-4 py-2 rounded-xl font-semibold">← Back</button>
          <h1 className="text-2xl font-bold">📰 News & Events Admin</h1>
        </div>
        <button onClick={onLogout} className="bg-gold text-white px-4 py-2 rounded-xl font-semibold">Logout</button>
      </header>

      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-4 items-center">
        <input
          type="text"
          placeholder="Search by title..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="px-4 py-2 border rounded-xl flex-1"
        />
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-4 py-2 border rounded-xl">
          <option value="all">All Types</option>
          <option value="news">News</option>
          <option value="events">Events</option>
          <option value="announcement">Announcements</option>
        </select>
        <button onClick={() => { setIsFormOpen(true); setEditingItem(null); }} className="px-4 py-2 bg-royal-blue text-white rounded-xl">+ Add News</button>
        {selectedItems.length > 0 && <button onClick={handleBulkDelete} className="px-4 py-2 bg-red-500 text-white rounded-xl">Delete Selected ({selectedItems.length})</button>}
      </div>

      {/* Loading Spinner */}
      {loading && (
        <div className="text-center py-4">
          <svg className="animate-spin h-8 w-8 text-royal-blue mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"></path>
          </svg>
          <p className="text-gray-500 mt-2">Loading news items...</p>
        </div>
      )}

      {/* News Table */}
      {!loading && (
        <div className="overflow-x-auto bg-white rounded-xl p-4 shadow-lg">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="p-2"><input type="checkbox" onChange={handleSelectAll} checked={selectedItems.length === newsItems.length && newsItems.length>0} /></th>
                <th className="p-2 text-left">Title</th>
                <th className="p-2 text-left">Type</th>
                <th className="p-2 text-left">Event Date</th>
                <th className="p-2 text-left">Location</th>
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredNews.map(item => (
                <tr key={item.id} className="border-b hover:bg-gray-50">
                  <td className="p-2 text-center">
                    <input type="checkbox" checked={selectedItems.includes(item.id)} onChange={() => handleSelectItem(item.id)} />
                  </td>
                  <td className="p-2">{item.title}</td>
                  <td className="p-2 capitalize">{item.type}</td>
                  <td className="p-2">{formatEventDate(item.start_date, item.end_date)}</td>
                  <td className="p-2">{item.location || '-'}</td>
                  <td className="p-2 flex gap-2">
                    <button onClick={() => { setEditingItem(item); setNewsForm(item); setIsFormOpen(true); }} className="px-3 py-1 bg-gold text-white rounded-xl">Edit</button>
                    <button onClick={() => handleDeleteItem(item.id)} className="px-3 py-1 bg-red-500 text-white rounded-xl">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasMore && <button onClick={loadNewsItems} className="mt-4 px-4 py-2 bg-royal-blue text-white rounded-xl">Load More</button>}
        </div>
      )}

      {/* Add/Edit Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-xl relative shadow-lg">
            <h2 className="text-xl font-bold mb-4">{editingItem ? 'Edit News Item' : 'Add News Item'}</h2>
            <input ref={titleInputRef} type="text" placeholder="Title" value={newsForm.title} onChange={e => setNewsForm({ ...newsForm, title: e.target.value })} className="w-full px-4 py-2 border rounded-xl mb-4" />
            <select value={newsForm.type} onChange={e => setNewsForm({ ...newsForm, type: e.target.value })} className="w-full px-4 py-2 border rounded-xl mb-4">
              <option value="news">News</option>
              <option value="events">Events</option>
              <option value="announcement">Announcements</option>
            </select>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <input type="date" value={newsForm.start_date} onChange={e => setNewsForm({ ...newsForm, start_date: e.target.value })} className="px-4 py-2 border rounded-xl" />
              <input type="date" value={newsForm.end_date} onChange={e => setNewsForm({ ...newsForm, end_date: e.target.value })} className="px-4 py-2 border rounded-xl" />
            </div>
            <input type="text" placeholder="Location" value={newsForm.location} onChange={e => setNewsForm({ ...newsForm, location: e.target.value })} className="w-full px-4 py-2 border rounded-xl mb-4" />
            <textarea rows={4} placeholder="Content" value={newsForm.content} onChange={e => setNewsForm({ ...newsForm, content: e.target.value })} className="w-full px-4 py-2 border rounded-xl mb-4"/>
            <input type="file" accept="image/*" onChange={handleImageChange} className="mb-4"/>
            {newsForm.imagePreview && <img src={newsForm.imagePreview} alt="Preview" className="mb-4 max-h-48 w-full object-cover rounded-xl" />}
            <div className="flex gap-2 justify-end">
              <button onClick={handleSaveNews} className="px-4 py-2 bg-royal-blue text-white rounded-xl">{uploadingImage ? 'Uploading...' : 'Save'}</button>
              <button onClick={resetForm} className="px-4 py-2 bg-gray-500 text-white rounded-xl">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {isConfirmOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-lg">
            <p className="mb-4">{confirmMessage}</p>
            <div className="flex justify-end gap-2">
              <button onClick={confirmAction} className="px-4 py-2 bg-red-500 text-white rounded-xl">Yes</button>
              <button onClick={() => setIsConfirmOpen(false)} className="px-4 py-2 bg-gray-500 text-white rounded-xl">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;
